import { useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";
import type { SpendDirection, SpendSort, SpendSupplierPage } from "@/types/spend";
import { estimateReason } from "../estimate-reasons";
import { estimateDisplay, moneyDisplay } from "../money-format";
import { spendQueryString } from "../use-spend-query-state";

const SupplierCategories = ({ categories }: { categories: string[] }) => {
  if (categories.length === 0) return <span className="text-gray-400">—</span>;
  if (categories.length === 1)
    return (
      <span className="block truncate" title={categories[0]}>
        {categories[0]}
      </span>
    );
  return <span title={categories.join("\n")}>{categories.length} categories</span>;
};

const SortButton = ({
  label,
  field,
  sort,
  direction,
  onSort,
}: {
  label: string;
  field: SpendSort;
  sort: SpendSort;
  direction: SpendDirection;
  onSort: (field: SpendSort) => void;
}) => (
  <button type="button" onClick={() => onSort(field)}>
    {label} {sort === field ? (direction === "asc" ? "↑" : "↓") : ""}
  </button>
);

export const SupplierSpendTable = ({
  data,
  sort,
  direction,
  onSort,
  onOffset,
}: {
  data: SpendSupplierPage;
  sort: SpendSort;
  direction: SpendDirection;
  onSort: (field: SpendSort) => void;
  onOffset: (offset: number) => void;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  return (
    <>
      <Table className="min-w-[920px]">
        <TableHeader className="bg-[#f7f2ec]">
          <TableRow>
            <TableHead>
              <SortButton label="Supplier" field="name" {...{ sort, direction, onSort }} />
            </TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="text-right">
              <SortButton label="Contract Families" field="family_count" {...{ sort, direction, onSort }} />
            </TableHead>
            <TableHead className="text-right">
              <SortButton label="Estimated (Annual, GBP)" field="estimated" {...{ sort, direction, onSort }} />
            </TableHead>
            <TableHead className="text-right">
              <SortButton label="Actual (GBP)" field="actual" {...{ sort, direction, onSort }} />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map(item => (
            <TableRow
              key={item.supplier_id}
              className="cursor-pointer"
              onClick={() =>
                navigate(`/spend-centre/suppliers/${item.supplier_id}?${spendQueryString(searchParams, {})}`)
              }
            >
              <TableCell>
                <button className="font-semibold text-[#111b35] hover:underline">{item.name}</button>
              </TableCell>
              <TableCell className="max-w-[18rem]">
                <SupplierCategories categories={item.categories ?? []} />
              </TableCell>
              <TableCell className="text-right tabular-nums">{item.family_count}</TableCell>
              <TableCell className="text-right tabular-nums">
                {estimateDisplay(item.estimated)}
                {item.estimated.reasons?.length ? (
                  <details className="text-xs text-amber-800">
                    <summary onClick={event => event.stopPropagation()}>Estimate details</summary>
                    {item.estimated.reasons.map(reason => (
                      <p key={reason}>{estimateReason(reason)}</p>
                    ))}
                  </details>
                ) : null}
              </TableCell>
              <TableCell className="text-right tabular-nums">{moneyDisplay(item.actual)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="grid gap-3 border-t border-[#e4ddd5] bg-[#faf7f3] px-5 py-4 sm:grid-cols-3 sm:items-center">
        <span className="text-sm font-medium">
          {data.total} Suppliers · {data.totals.family_count} Contract Families
        </span>
        <div className="text-center text-sm">
          <strong>Total:</strong> {moneyDisplay(data.totals.actual)} <span className="text-gray-500">actual</span> ·{" "}
          {estimateDisplay(data.totals.estimated)} <span className="text-gray-500">estimated</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={data.offset === 0}
            onClick={() => onOffset(Math.max(0, data.offset - data.limit))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={data.offset + data.limit >= data.total}
            onClick={() => onOffset(data.offset + data.limit)}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};
